import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Share2, Facebook, MessageCircle, Send, Link, Check } from "lucide-react";

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  className?: string;
}

const ShareButtons = ({ url, title, description, className }: ShareButtonsProps) => {
  const [copied, setCopied] = useState(false);

  const shareData = {
    url: encodeURIComponent(url),
    title: encodeURIComponent(title),
    description: encodeURIComponent(description || title)
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareData.url}&quote=${shareData.title}`,
    whatsapp: `https://wa.me/?text=${shareData.title}%20${shareData.url}`,
    telegram: `https://t.me/share/url?url=${shareData.url}&text=${shareData.title}`,
    twitter: `https://twitter.com/intent/tweet?url=${shareData.url}&text=${shareData.title}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareData.url}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({
        title: "¡Enlace copiado!",
        description: "El enlace se ha copiado al portapapeles",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopied(true);
      toast({
        title: "¡Enlace copiado!",
        description: "El enlace se ha copiado al portapapeles",
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openShareWindow = (url: string) => {
    window.open(
      url,
      'share-dialog',
      'width=600,height=400,resizable=yes,scrollbars=yes'
    );
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="text-xs text-muted-foreground mr-2">Compartir:</span>
      
      {/* Native share button (mobile) */}
      {navigator.share && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleNativeShare}
          className="flex items-center gap-1 px-2 py-1 h-7 text-xs"
        >
          <Share2 className="h-3 w-3" />
          <span className="hidden sm:inline">Compartir</span>
        </Button>
      )}

      {/* Facebook */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => openShareWindow(shareLinks.facebook)}
        className="flex items-center gap-1 px-2 py-1 h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
      >
        <Facebook className="h-3 w-3" />
        <span className="hidden md:inline">Facebook</span>
      </Button>

      {/* WhatsApp */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => openShareWindow(shareLinks.whatsapp)}
        className="flex items-center gap-1 px-2 py-1 h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
      >
        <MessageCircle className="h-3 w-3" />
        <span className="hidden md:inline">WhatsApp</span>
      </Button>

      {/* Telegram */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => openShareWindow(shareLinks.telegram)}
        className="flex items-center gap-1 px-2 py-1 h-7 text-xs text-blue-500 hover:text-blue-600 hover:bg-blue-50"
      >
        <Send className="h-3 w-3" />
        <span className="hidden md:inline">Telegram</span>
      </Button>

      {/* Twitter */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => openShareWindow(shareLinks.twitter)}
        className="flex items-center gap-1 px-2 py-1 h-7 text-xs text-sky-500 hover:text-sky-600 hover:bg-sky-50"
      >
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        <span className="hidden md:inline">Twitter</span>
      </Button>

      {/* Copy Link */}
      <Button
        variant="outline"
        size="sm"
        onClick={copyToClipboard}
        className={`flex items-center gap-1 px-2 py-1 h-7 text-xs transition-colors ${
          copied 
            ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
            : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
        }`}
      >
        {copied ? <Check className="h-3 w-3" /> : <Link className="h-3 w-3" />}
        <span className="hidden md:inline">
          {copied ? 'Copiado' : 'Copiar'}
        </span>
      </Button>
    </div>
  );
};

export default ShareButtons;